// config-backend's public auth API at `nativeSignIn.apiBaseUrl`: cross-origin JSON (ACT2-871). Only the calls that start
// or read the shared session send credentials, because its cookie lives on the API host (ACT2-879).
import type { SignupRegion } from '@server/signup-answers';
import { z } from 'zod';

import { SSO_REQUIRED, WORK_EMAIL_REQUIRED } from './sign-in-errors';

export type MfaMethod = 'TOTP' | 'SMS' | 'BACKUP_CODE';

const MFA_METHODS: readonly string[] = ['TOTP', 'SMS', 'BACKUP_CODE'] satisfies MfaMethod[];

export interface MfaChallenge {
  sessionToken: string;
  /** The method the code step opens on. */
  method: MfaMethod;
  availableMethods: MfaMethod[];
}

/** An email and password, or the sign-up link token and the password just set. */
export type LoginCredentials = { email: string; password: string } | { token: string; password: string };

export type LoginRequest =
  | LoginCredentials
  | (LoginCredentials & { sessionToken: string; code: string; method: MfaMethod })
  | (LoginCredentials & { sessionToken: string; sendSmsOtp: true });

export type LoginResult =
  // With the shared session, config-backend keeps the refresh token, so the reply has none.
  | { kind: 'tokens'; idToken: string; refreshToken?: string }
  | { kind: 'mfa_challenge'; challenge: MfaChallenge }
  | { kind: 'mfa_setup' };

/** A refusal from config-backend, carrying the message to show. `reason` is config-backend's own text. */
export class RudderstackAuthError extends Error {
  readonly status: number;
  readonly reason: string | undefined;

  constructor(message: string, status: number, reason?: string) {
    super(message);
    this.name = 'RudderstackAuthError';
    this.status = status;
    this.reason = reason;
  }
}

const GENERIC_FAILURE = 'Something went wrong. Please try again.';
const UNREACHABLE = 'Could not reach RudderStack. Check your connection and try again.';
const TOO_MANY_ATTEMPTS = 'Too many attempts. Wait a few minutes and try again.';
const INVALID_CODE = 'That code is not valid.';
/** The first sentence of the account-exists message. The sign-up completion page links "Sign in" after it. */
export const ACCOUNT_EXISTS_TEXT = 'This email is already associated with a RudderStack account.';
const ACCOUNT_EXISTS = `${ACCOUNT_EXISTS_TEXT} Sign in instead.`;

/** config-backend's texts that Lookout replaces, the same on every page. Other texts show as config-backend sends them. */
const LOOKOUT_MESSAGES: Record<string, string> = {
  'Password attempts exceeded':
    'Your account is temporarily locked due to too many failed password attempts. Please try again later.',
  'User must use SSO': SSO_REQUIRED,
  'Try logging in with Google': 'Please sign in with Google.',
  'Incorrect username or password.': 'Wrong email or password.',
  'User already Signed up. Go to login': ACCOUNT_EXISTS,
  'User already signed up': ACCOUNT_EXISTS,
  'Your email has SSO configured. Log in through SSO': SSO_REQUIRED,
  'Account already exists. Please sign in with SSO instead.': SSO_REQUIRED,
  'Provided email is not a valid work email': WORK_EMAIL_REQUIRED,
  'Your domain is blacklisted': 'Sign-up is not allowed for this email domain.',
};

const TokensSchema = z.object({ idToken: z.string().min(1), refreshToken: z.string().min(1).optional() });

const LoginBodySchema = z.discriminatedUnion('status', [
  TokensSchema.extend({ status: z.literal('success') }),
  // Inside the MFA grace period the tokens arrive too, so this signs in like a success.
  TokensSchema.extend({ status: z.literal('mfa_grace_period') }),
  z.object({
    status: z.literal('mfa_challenge'),
    sessionToken: z.string().min(1),
    mfaMethod: z.string().optional(),
    availableMethods: z.array(z.string()).optional(),
  }),
  z.object({ status: z.literal('mfa_setup') }),
]);

const MessageBodySchema = z.object({ message: z.string().min(1) });
const StatusCodeBodySchema = z.object({ statusCode: z.number() });
const SsoIdentifierSchema = z.object({ identifier: z.string().min(1) });

interface RawResponse {
  status: number;
  ok: boolean;
  body: unknown;
}

async function send(
  apiBaseUrl: string,
  path: string,
  body?: unknown,
  credentials: RequestCredentials = 'omit',
): Promise<RawResponse> {
  let res: Response;
  try {
    res = await fetch(`${apiBaseUrl.replace(/\/+$/, '')}${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      credentials,
      ...(body === undefined ? {} : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
    });
  } catch {
    throw new RudderstackAuthError(UNREACHABLE, 0);
  }
  const parsed: unknown = await res.json().catch(() => null);
  // `/signup` and `/confirmNewPassword` can report a failure in the body's `statusCode`.
  const bodyStatus = StatusCodeBodySchema.safeParse(parsed);
  const failedInBody = bodyStatus.success && bodyStatus.data.statusCode >= 400;
  return { status: failedInBody ? bodyStatus.data.statusCode : res.status, ok: res.ok && !failedInBody, body: parsed };
}

function refusal(res: RawResponse): RudderstackAuthError {
  if (res.status === 429) return new RudderstackAuthError(TOO_MANY_ATTEMPTS, res.status);
  const parsed = MessageBodySchema.safeParse(res.body);
  if (!parsed.success) return new RudderstackAuthError(GENERIC_FAILURE, res.status);
  const reason = parsed.data.message;
  // `Object.hasOwn`, so that a reply such as `constructor` cannot reach Object.prototype.
  const message = Object.hasOwn(LOOKOUT_MESSAGES, reason) ? LOOKOUT_MESSAGES[reason]! : reason;
  return new RudderstackAuthError(message, res.status, reason);
}

function isMfaMethod(value: string): value is MfaMethod {
  return MFA_METHODS.includes(value);
}

function toChallenge(sessionToken: string, mfaMethod?: string, availableMethods: string[] = []): MfaChallenge {
  const methods = availableMethods.filter(isMfaMethod);
  const method = mfaMethod && isMfaMethod(mfaMethod) ? mfaMethod : methods[0];
  if (!method) throw new RudderstackAuthError(GENERIC_FAILURE, 200);
  return { sessionToken, method, availableMethods: methods.includes(method) ? methods : [method, ...methods] };
}

/**
 * `POST /login`: the password sign-in, both MFA phases, and the sign-in right after sign-up. With `sharedSession`, a
 * reply with tokens also starts config-backend's shared session, and the browser keeps its cookie.
 */
export async function rudderstackLogin(
  apiBaseUrl: string,
  request: LoginRequest,
  options: { sharedSession?: boolean } = {},
): Promise<LoginResult> {
  const res = options.sharedSession
    ? await send(apiBaseUrl, '/login', { ...request, sharedSession: true }, 'include')
    : await send(apiBaseUrl, '/login', request);
  if (!res.ok) {
    // A 403 on a code submission is a wrong or expired code.
    if (res.status === 403 && 'code' in request) throw new RudderstackAuthError(INVALID_CODE, res.status);
    throw refusal(res);
  }
  const parsed = LoginBodySchema.safeParse(res.body);
  if (!parsed.success) throw new RudderstackAuthError(GENERIC_FAILURE, res.status);
  const body = parsed.data;
  switch (body.status) {
    case 'success':
    case 'mfa_grace_period':
      return {
        kind: 'tokens',
        idToken: body.idToken,
        ...(body.refreshToken ? { refreshToken: body.refreshToken } : {}),
      };
    case 'mfa_challenge':
      return {
        kind: 'mfa_challenge',
        challenge: toChallenge(body.sessionToken, body.mfaMethod, body.availableMethods),
      };
    case 'mfa_setup':
      return { kind: 'mfa_setup' };
  }
}

/**
 * Lookout never offers or requests an open-source sign-up. config-backend's `freetrial` picks the plan, not a
 * trial: true is Free Tier, false is the open-source plan. config-backend skips the automatic trial for Lookout.
 */
const FREE_TIER_PLAN = { freetrial: true } as const;

/** `POST /signupMail`: emails the link that finishes sign-up; a Lookout invitee adds the invite marker. */
export async function sendSignupEmail(apiBaseUrl: string, input: { email: string; invite: boolean }): Promise<void> {
  const res = await send(apiBaseUrl, '/signupMail', {
    email: input.email,
    redirectTo: 'lookout',
    // Tells config-backend that this page can finish the signup, so the emailed link opens Lookout.
    lookoutSignupPage: true,
    ...FREE_TIER_PLAN,
    ...(input.invite ? { lookoutInvite: true } : {}),
  });
  if (!res.ok) throw refusal(res);
}

/** `POST /signup`: creates the account behind a sign-up link. An invitee sends no organization. */
export async function createRudderstackAccount(
  apiBaseUrl: string,
  input: { name: string; token: string; password: string; organization?: string; region?: SignupRegion },
): Promise<void> {
  const res = await send(apiBaseUrl, '/signup', {
    name: input.name,
    token: input.token,
    password: input.password,
    ...(input.organization === undefined ? {} : { organization: input.organization }),
    ...(input.region === undefined ? {} : { region: input.region }),
    verified: true,
    ...FREE_TIER_PLAN,
  });
  if (!res.ok) throw refusal(res);
}

/** `POST /forgotPassMail`: the answer is the same whether or not the email has an account. */
export async function sendPasswordResetEmail(apiBaseUrl: string, email: string): Promise<void> {
  const res = await send(apiBaseUrl, '/forgotPassMail', { email, redirectTo: 'lookout' });
  if (!res.ok) throw refusal(res);
}

/** config-backend's refusals of a reset link that is too old, already used or damaged. */
const RESET_LINK_REFUSALS = new Set(['Token expired', 'Token has expired', 'Please provide a valid token']);
const RESET_LINK_INVALID = 'This reset link is no longer valid. Request a new link.';

/** `POST /confirmNewPassword`: sets the password behind a reset link. */
export async function setNewPassword(apiBaseUrl: string, input: { token: string; password: string }): Promise<void> {
  const res = await send(apiBaseUrl, '/confirmNewPassword', { token: input.token, password: input.password });
  if (res.ok) return;
  const error = refusal(res);
  if (error.reason !== undefined && RESET_LINK_REFUSALS.has(error.reason)) {
    throw new RudderstackAuthError(RESET_LINK_INVALID, error.status, error.reason);
  }
  throw error;
}

/** `GET /v1/sso/idp`: the SSO identity provider for this email, or null when it has none. */
export async function findSsoIdentifier(apiBaseUrl: string, email: string): Promise<string | null> {
  const res = await send(apiBaseUrl, `/v1/sso/idp?${new URLSearchParams({ email }).toString()}`);
  if (res.status === 404) return null;
  if (!res.ok) throw refusal(res);
  const parsed = SsoIdentifierSchema.safeParse(res.body);
  if (!parsed.success) throw new RudderstackAuthError(GENERIC_FAILURE, res.status);
  return parsed.data.identifier;
}

const HandoffBodySchema = z.object({ code: z.string().min(1) });
const SignOutBodySchema = z.object({ hostedUiSignOutUrl: z.string().min(1).optional() });

/** A one-time code for Lookout's server, or why there is none. `ended` is the only answer that means "no session". */
export type SessionHandoff = { kind: 'code'; code: string } | { kind: 'ended' } | { kind: 'unavailable' };

/**
 * `POST /session/handoff`: a one-time code from config-backend's shared session (ACT2-879). Lookout's server redeems
 * it on config-backend's internal port, so it never accepts an ID token from this page.
 */
export async function getSessionHandoff(apiBaseUrl: string): Promise<SessionHandoff> {
  let res: RawResponse;
  try {
    res = await send(apiBaseUrl, '/session/handoff', {}, 'include');
  } catch {
    return { kind: 'unavailable' };
  }
  if (res.status === 401) return { kind: 'ended' };
  const parsed = HandoffBodySchema.safeParse(res.body);
  return res.ok && parsed.success ? { kind: 'code', code: parsed.data.code } : { kind: 'unavailable' };
}

/**
 * `POST /session/federated`: starts the shared session after a Google or SSO sign-in. config-backend refreshes once to
 * prove the refresh token. The page keeps none of the tokens: Lookout's session starts from a one-time code.
 */
export async function startFederatedSession(
  apiBaseUrl: string,
  tokens: { idToken: string; refreshToken: string },
): Promise<void> {
  const res = await send(apiBaseUrl, '/session/federated', tokens, 'include');
  if (!res.ok) throw refusal(res);
}

/**
 * `POST /session/signout`: ends the shared session, so both sites sign out. A Google or SSO session also names the
 * Hosted UI sign-out page.
 */
export async function endSharedSession(apiBaseUrl: string): Promise<{ hostedUiSignOutUrl: string | null }> {
  let res: RawResponse;
  try {
    res = await send(apiBaseUrl, '/session/signout', {}, 'include');
  } catch (error) {
    // CORS hides config-backend's 403 from a site that it does not trust, so the browser reports no status. A call
    // without credentials shows that answer, and it ends nothing.
    const probe = await send(apiBaseUrl, '/session/signout', {}, 'omit').catch(() => null);
    if (probe && (probe.status === 403 || probe.status === 415)) throw refusal(probe);
    throw error;
  }
  if (!res.ok) throw refusal(res);
  const parsed = SignOutBodySchema.safeParse(res.body);
  return { hostedUiSignOutUrl: parsed.success ? (parsed.data.hostedUiSignOutUrl ?? null) : null };
}
